<template>
  <div
    ref="dropdownRef"
    :class="formKitProp.context!.classes.dropdownWrapper">
    <button
      ref="selectButtonRef"
      type="button"
      class="search-dropdown__selected"
      :class="[formKitProp.context!.classes.selected, props.small]"
      :tabindex="dropdown.focused ? -1 : 0"
      :disabled="props.disabled.value"
      :data-testid="formKitProp.context.attrs['data-testid']"
      @click="handleButtonClick"
      @focus="selected || selectSearchRef?.focus()"
      @keydown.up.prevent="dropdown.close"
      @keydown.down.prevent="focusOnDown()">
      <input
        v-if="!props.innerSearch"
        v-show="dropdown.isVisible || (dropdown.focused && !selected)"
        ref="selectSearchRef"
        v-model="searchString"
        class="outline-none"
        :placeholder="$t('placeholder.input.search')"
        @input="dropdown.open()"
        @click.stop
        @keydown.stop
        @keydown.up.prevent.stop="dropdown.close(); selectButtonRef.focus()"
        @keydown.down.prevent.stop="dropdown.open(); dropdown.focusOption(0)"
        @keydown.enter.prevent="selectOption(filtered[0] as any)"/>
      <div
        v-show="!(!props.innerSearch && (dropdown.isVisible || (dropdown.focused && !selected)))"
        class="text-old-n-black-100 overflow-hidden text-start">
        <slot
          v-if="selected"
          name="selected"
          v-bind="{ selected }">
          {{ $t(getLabel(selected as any) || '') }}
        </slot>
        <div
          v-else
          class="text-old-n-black-60">
          {{ $t(props.placeholder.value) }}
        </div>
      </div>
      <img
        class="search-dropdown__chevron"
        :class="{'rotate-180': dropdown.isVisible}"
        :src="chevronIcon"/>
    </button>
    <div
      v-show="dropdown.isVisible"
      class="search-dropdown__list scrollbar-thin">
      <input
        v-if="props.innerSearch"
        ref="innerSearchRef"
        v-model="searchString"
        type="text"
        tabindex="-1"
        autocomplete="off"
        @keydown.up.prevent="dropdown.close(); selectButtonRef.focus()"
        @keydown.down.prevent="dropdown.focusOption(0)"
        @keydown.enter.prevent="selectOption(filtered[0] as any)"/>
      <ul
        ref="itemContainerRef"
        v-loading="isLoadingSearcher"
        class="search-dropdown__list-options"
        :data-empty="filtered.length === 0"
        tabindex="-1">
        <li
          v-for="(option, index) in filtered"
          ref="optionRefs"
          :key="index"
          role="option"
          tabindex="-1"
          :class="formKitProp.context!.classes.option"
          @keydown.up.ctrl.prevent="(props.innerSearch ? innerSearchRef : selectSearchRef).focus()"
          @keydown.up.exact.prevent="dropdown.focusOption(index - 1, props.innerSearch ? innerSearchRef : selectSearchRef)"
          @keydown.down.prevent="dropdown.focusOption(index + 1)"
          @keydown.enter.prevent="selectOption(option as any)"
          @click="selectOption(option as any)">
          <slot
            name="option"
            v-bind="{ option: option }">
            {{ $t(getLabel(option as any)) }}
          </slot>
        </li>
        <!-- DEBT: [Major] Rewrite styles: -mt-24 cause layout shift -->
        <li
          v-if="props.onLoad !== undefined"
          v-intersection-observer="loadOnScroll"
          class="-mt-24"/>
        <li
          v-if="isLoading"
          v-loading="isLoading"
          class="h-24"/>
      </ul>
      <div
        v-if="filtered.length === 0"
        v-loading="isLoadingSearcher"
        class="my-auto">
        {{ $t('components.Dropdown.empty') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { vIntersectionObserver } from '@vueuse/components';
import {
  computed, nextTick, ref, watch,
} from 'vue';

import chevronIcon from '@/assets/icons/chevron.svg';
import { WithFormKitContext } from '@/components/formkit/types';
import { DropdownReturnType, useDropdown } from '@/composables/useDropdown';
import { useUIDropdown } from '@/composables/useUIDropdown';
import { i18n } from '@/i18n';
import { isBrowser } from '@/utils/isBrowser';

import { DropdownSearcher, getFuseDropdownSearcher } from './SearchDropdown/DropdownSearcher';

// DEBT: [Minor] Rewrite inline styles
// DEBT: [Minor] Rewrite style to formkit style or BEM

type DropdownReturn = ReturnType<typeof getFormKitValue>;
interface ContextProps {
  options: T | T[];
  searcher?: DropdownSearcher<T>;
  onChange?: (value: DropdownReturn) => void;
}

const formKitProp = defineProps<WithFormKitContext<ContextProps>>();
const props = {
  options: computed(() => formKitProp.context.options as T[] ?? []),
  value: computed(() => formKitProp.context.value),
  placeholder: computed(() => formKitProp.context.placeholder as string ?? 'components.Dropdown.not_selected'),
  valueKey: formKitProp.context.valueKey as keyof T | undefined,
  labelKey: formKitProp.context.labelKey as keyof T | undefined,
  innerSearch: Boolean(formKitProp.context.innerSearch || formKitProp.context.innerSearch === ''),
  disabled: computed(() => Boolean(formKitProp.context.disabled || formKitProp.context.disabled === '')),
  small: formKitProp.context.small !== undefined ? 'small' : '',
  onChange: (value: DropdownReturn) => formKitProp.context?.onChange?.(value),
  onLoad: (formKitProp.context.onLoad as () => Promise<void> | undefined),
  searcher: formKitProp.context.searcher ?? getFuseDropdownSearcher<T>({
    threshold: 0.1,
    keys: [{
      name: 'label',
      getFn: (item: any) => (item[props.labelKey || 'label']
        ? i18n.global.t(item[props.labelKey || 'label'])
        : ''),
    }],
  }),
};

const dropdownRef = ref();
const selectButtonRef = ref();
const selectSearchRef = ref();
const innerSearchRef = ref();
const optionRefs = ref();
const isLoading = ref<boolean>(false);

const { filtered, searchString, isLoading: isLoadingSearcher } = props.searcher(props.options, props.labelKey);

const dropdown = useUIDropdown({
  wrapperEl: dropdownRef,
  optionRefs,
  onClose() {
    searchString.value = '';
  },
});

const {
  selected,
  selectOption,
  getLabel,
  getFormKitValue,
  setSelectedByFormKitValue,
}: DropdownReturnType<T> = useDropdown<T>({
  options: props.options,
  value: props.value.value,
  valueKey: props.valueKey,
  labelKey: props.labelKey,
  onSelect() {
    dropdown.close();
    selectButtonRef.value.focus();
    searchString.value = '';
  },
}) as any;

function focusOnDown() {
  dropdown.open();
  if (props.innerSearch) {
    nextTick(() => innerSearchRef.value.focus());
    return;
  }
  if (selected.value) {
    nextTick(() => selectSearchRef.value.focus());
    return;
  }
  dropdown.focusOption(0);
}

function handleButtonClick(event: MouseEvent) {
  if (!(event as PointerEvent).pointerType && !(isBrowser.firefox || isBrowser.safari)) return;

  dropdown.toggle();
  nextTick(() => {
    if (dropdown.isVisible) selectSearchRef.value?.focus();
    innerSearchRef.value?.focus();
  });
}

const value = computed(() => formKitProp.context.value);
watch(value, (newVal) => {
  if (newVal === getFormKitValue()) return;
  setSelectedByFormKitValue(newVal);
});

formKitProp.context.node.input(getFormKitValue());
watch(selected, async () => {
  const newValues = getFormKitValue();
  await formKitProp.context.node.input(newValues);
  props.onChange?.(newValues);
}, { deep: true });

async function loadOnScroll([{ isIntersecting }]: IntersectionObserverEntry[]) {
  if (!isIntersecting) return;

  isLoading.value = true;
  await props.onLoad();
  isLoading.value = false;
}
</script>

<style lang="scss">
[data-type="SearchDropdown"] {
  & > .formkit-wrapper > .formkit-inner {
    @apply shadow-none;
  }
}

.formkit-outer[data-invalid="true"],
.formkit-outer[data-errors="true"] {
  .search-dropdown {
    &__selected {
      @apply ring-old-n-red-100 #{!important};
    }
  }
}

.formkit-dropdown-wrapper {
  @apply relative w-full;
  @apply [&>:first-child]:focus-within:ring-2 [&>:first-child]:focus-within:ring-old-black;
}

.formkit-selected {
  @apply grid grid-cols-[calc(100%-32px)_24px] items-center gap-8 w-full h-[48px] py-8 px-16;
  @apply leading-[1.375rem] ring-1 ring-old-n-gray-40 ring-inset rounded;
  @apply disabled:ring-0 disabled:bg-old-n-gray-10 disabled:text-old-n-black-50 [&>img]:disabled:opacity-45;
  @apply focus-visible:outline-none;

  input {
    @apply disabled:bg-old-n-gray-10;
  }

  &.small {
    @apply h-[40px] text-base;
  }
}

.formkit-option {
  @apply flex items-center gap-10 h-48 mr-8 py-8 px-10 rounded-lg cursor-pointer;
  @apply hover:bg-old-n-gray-10 active:bg-old-n-gray-20;
  @apply focus-visible:outline-none focus-visible:bg-old-n-gray-10 focus:bg-old-n-gray-10 break-words overflow-hidden;

  .icon {
    @apply inline-flex items-center justify-center w-20 h-20 ml-2;
    @apply text-[7px] text-old-white leading-5;
    @apply border-2 border-old-n-gray-80 rounded-4;

    &.icon-save {
      @apply bg-old-n-black-100 border-old-n-black-100;
    }
  }
}

.search-dropdown {
  &__chevron {
    @apply w-24 h-24 ml-auto transition transform;
  }

  &__list {
    @apply absolute top-56 flex flex-col w-full pl-12 pr-4 py-8 border border-old-n-gray-40 rounded bg-old-white z-10 overflow-hidden;

    &-options {
      @apply max-h-[14.75rem] min-h-48 overflow-x-auto;

      img {
        @apply w-24 h-24;
      }
    }

    input[type="text"] {
      @apply w-full h-32 py-8 mb-4 text-lg border-b border-old-n-gray-40 box-border outline-none;
    }

    &__loader {
      @apply absolute bottom-0 right-0 flex w-full h-4 bg-old-black bg-opacity-[10%];

      --uib-speed: 1.4s;
      // --uib-bg-opacity: 0.1;

      &::after {
        @apply w-full h-full rounded-32 bg-old-fill-accent-primary-default;

        content: "";
        animation: zoom var(--uib-speed) ease-in-out infinite;
      }
    }
  }
}

@keyframes zoom {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}
</style>
